"use client";
import fetchData from "libs/fetch";
import { GET_GALLERY_IMAGES } from "api";
import { useInfiniteQuery } from "@tanstack/react-query";

export interface GalleryImage {
  _id: string;
  file_name: string;
  upscaled: boolean;
  status: string;
  nsfw: boolean;
  img2img: boolean;
  is_starred: boolean;
  approved_for_gallery: boolean;
  created_at: string;
}

export interface GalleryImageResponse {
  gallery_images: GalleryImage[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Fetch gallery images with pagination support
 *
 * @param pageParam - Current page number
 * @param pageSize - Items per page
 * @returns The gallery image response
 */
const fetchGalleryImages = async (
  pageParam: number = 1,
  pageSize: number
): Promise<GalleryImageResponse> => {
  const queryString = `page=${pageParam}&page_size=${pageSize}`;
  return fetchData(`${GET_GALLERY_IMAGES}?${queryString}`);
};

export const useGetGalleryImages = (pageSize: number = 20) => {
  return useInfiniteQuery<GalleryImageResponse, Error>({
    queryKey: ["gallery-images", pageSize],
    queryFn: ({ pageParam = 1 }) =>
      fetchGalleryImages(pageParam as number, pageSize),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const totalPages = Math.ceil(lastPage.total / pageSize);
      return nextPage <= totalPages ? nextPage : undefined;
    },
    initialPageParam: 1,
  });
};

export default useGetGalleryImages;
